// apps/accounts/src/pages/staff/StaffMaterials.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Package, RefreshCw, AlertCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffMaterials() {
  const [materials, setMaterials] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const data = await pb.collection('materials').getFullList({
        sort: 'name',
        expand: 'category,uom',
      });
      setMaterials(data);
      setFiltered(data);
    } catch (err) {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  useEffect(() => {
    if (search.trim() === '') {
      setFiltered(materials);
    } else {
      const q = search.toLowerCase();
      setFiltered(materials.filter(m => 
        m.name.toLowerCase().includes(q) || 
        m.code?.toLowerCase().includes(q)
      ));
    }
  }, [search, materials]);

  const handleStockUpdate = async (id, newStock) => {
    const val = parseFloat(newStock);
    if (isNaN(val) || val < 0) {
      toast.error('Enter a valid positive number');
      return;
    }
    setUpdating(true);
    try {
      await pb.collection('materials').update(id, { current_stock: val });
      toast.success('Stock updated');
      fetchMaterials();
      setEditing(null);
    } catch (err) {
      toast.error('Update failed: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Materials Stock</h1>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search materials..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">No materials found</CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map(mat => (
            <Card key={mat.id} className="border-l-4 border-l-amber-400">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex-1 min-w-[150px]">
                  <p className="font-semibold text-slate-800">{mat.name}</p>
                  <p className="text-sm text-slate-500">
                    {mat.expand?.category?.name || 'No category'} � {mat.expand?.uom?.symbol || 'unit'}
                  </p>
                  {mat.code && <p className="text-xs text-slate-400">Code: {mat.code}</p>}
                </div>

                <div className="flex items-center gap-4">
                  {editing === mat.id ? (
                    <>
                      <Input
                        type="number"
                        defaultValue={mat.current_stock}
                        className="w-24"
                        id={`stock-${mat.id}`}
                        min="0"
                        step="0.01"
                      />
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          const val = document.getElementById(`stock-${mat.id}`).value;
                          handleStockUpdate(mat.id, val);
                        }}
                        disabled={updating}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="text-xl font-bold text-slate-800">{mat.current_stock}</span>
                      {mat.current_stock < mat.min_stock && (
                        <AlertCircle className="w-5 h-5 text-amber-500" title="Low stock" />
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(mat.id)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" /> Update
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}